import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import { formatMoney } from 'common'

import { DuplicateExpense, DuplicatePremium } from 'core'
import { ExpenseStatus } from 'core/domain/expenses/expenseStatus'

import { Alert, Icon, Modal } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { ROUTE_PATHS } from 'common/routePaths'

const RedirectLink = ({ handleClick }: { handleClick: () => void }) => (
  <a className="underlined color-black" onClick={handleClick}>
    Click here
  </a>
)

const BoldText = ({ text }: { text: string }) => <span className="semi-bold">{text}</span>

const mapExpenseStatus = new Map<number, string>([
  [ExpenseStatus.AwaitingReview, 'Awaiting Review'],
  [ExpenseStatus.Verified, 'Verified'],
  [ExpenseStatus.ActionRequired, 'Action Required'],
  [ExpenseStatus.Ineligible, 'Ineligible'],
  [ExpenseStatus.AwaitingAdminReview, 'Awaiting Admin Review'],
])

interface Props {
  duplicateExpense: DuplicateExpense | DuplicatePremium | undefined
  visible: boolean
  onCancel: (cancelSubmit: boolean) => void
  onOk: () => void
}

const DuplicateExpenseModal = ({ duplicateExpense, visible, onCancel, onOk }: Props) => {
  const { t: translate } = useTranslation()
  const location = useLocation()
  const history = useHistory()

  const premiumId = (duplicateExpense as DuplicatePremium)?.premiumId

  const redirectLink = (() => {
    const statusIsActionRequired = duplicateExpense?.status === ExpenseStatus.ActionRequired

    if (!statusIsActionRequired) return ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD

    const newLocation = premiumId ? ROUTE_PATHS.ELIGIBLE_EMPLOYEE_EDIT_PREMIUM_EXPENSE : location.pathname

    const searchParams = new URLSearchParams(location.search)
    statusIsActionRequired &&
      (premiumId
        ? (searchParams.set('recurringExpenseId', duplicateExpense.id), searchParams.set('premiumId', premiumId))
        : searchParams.set('expenseId', duplicateExpense.id))

    return `${newLocation}?${searchParams.toString()}`
  })()

  const typeText = premiumId ? 'premium' : 'expense'
  const typeTextUpperCase = typeText.charAt(0).toUpperCase() + typeText.slice(1)

  const handleLinkClick = () => {
    history.push(redirectLink)
    onCancel(true)
    window.location.reload()
  }

  return (
    <Modal
      visible={visible}
      title={`Potential Duplicate ${typeTextUpperCase}`}
      cancelText="Back to edit"
      onCancel={() => onCancel(true)}
      okText="Continue with submission"
      onOk={() => {
        onOk()
        onCancel(false)
      }}
    >
      {visible && (
        <>
          <p className="semi-bold">
            It looks like you are trying to submit a duplicate {typeText}. Do you want to continue with submission?
          </p>
          <p className="color-gray-7">
            You can only submit one reimbursement request for each {typeText}. If this is not a duplicate, you can
            continue.
          </p>
          <Alert
            showIcon
            icon={<Icon fontAwesome type="fas fa-exclamation-triangle" className="color-yellow-7 mr-8" />}
            type="warning"
            message={
              <div>
                {<RedirectLink handleClick={handleLinkClick} />} to view your prior submission for the amount of
                {<BoldText text={` ${formatMoney(duplicateExpense.requestedAmount)}`} />}, with service{' '}
                {premiumId ? 'start' : ''} date of
                {
                  <BoldText
                    text={` ${parseZoneFormat(
                      premiumId
                        ? (duplicateExpense as DuplicatePremium).startDate
                        : (duplicateExpense as DuplicateExpense).purchasedDate,
                    )}`}
                  />
                }
                . It was submitted on
                {<BoldText text={` ${parseZoneFormat(duplicateExpense.createdAt)}`} />} and has a status of
                {<BoldText text={` ${translate(mapExpenseStatus.get(duplicateExpense.status))}`} />}
                {duplicateExpense.lastReview !== null && (
                  <BoldText text={` as of ${parseZoneFormat(duplicateExpense.lastReview.createdAt)}`} />
                )}
                .
                <br />
                {duplicateExpense.status === ExpenseStatus.AwaitingReview &&
                  `Submissions are reviewed in the order they are submitted. Submitting a duplicate ${typeText} will slow down the process of getting your initial ${typeText} verified.`}
              </div>
            }
          />
        </>
      )}
    </Modal>
  )
}

export default DuplicateExpenseModal
